import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Avatar, Username, VipBadge } from "../components/UserBadge";
import { BANNER_BADGES, WEAPONS, AVATAR_FRAMES, USERNAME_STYLES, BACKGROUND_VIDEOS } from "../constants/loadout";
import AnimatedBackground from "../components/AnimatedBackground";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Edit, MapPin, Calendar, Award, MessageSquare, Heart, Crosshair, Crown,
  Gamepad2, Camera, Image as ImageIcon, Hash, GripVertical, Trophy,
  Sparkles, Save, X, User, Users, Clock, Monitor, MessageCircle, Shield, Edit3,
  Home, Flag, UserPlus, UserMinus, Send, ChevronDown, Check, Play, Music, Eye, ArrowUpRight
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { renderMarkdown } from "../components/ReplyEditor";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "../components/VipLoadout.css";
import StandardProfileHeader from "./StandardProfileHeader";

const TROPHY_MAP = {
  first_post: { label: "Prima postare", icon: "🎯" },
  ten_posts: { label: "10 postări", icon: "💬" },
  hundred_posts: { label: "100 postări", icon: "📨" },
  five_hundred_posts: { label: "500 postări", icon: "🏅" },
  first_reaction: { label: "Prima reacție", icon: "👍" },
  fifty_reactions: { label: "50 reacții", icon: "🔥" },
  hundred_reactions: { label: "100 reacții", icon: "💯" },
  first_topic: { label: "Primul topic", icon: "📌" },
  ten_topics: { label: "10 topicuri", icon: "📚" },
  vip_member: { label: "VIP Member", icon: "👑" },
  veteran: { label: "Veteran", icon: "🎖️" },
  elite: { label: "Elite", icon: "💎" },
  founder: { label: "Founder", icon: "⭐" },
  donator: { label: "Donator", icon: "💖" },
  monthly_winner: { label: "Câștigător lunar", icon: "🏆" },
};

const VIP_TIER_CONFIG = {
  bronze: {
    label: "VIP BRONZE",
    badgeClass: "userBanner userBanner--bronze",
    color: "#cd7f32",
    accentGrad: "from-[#cd7f32] via-[#e5a65d] to-[#cd7f32]",
    cardBorder: "border-[#cd7f32]/60 shadow-[0_15px_40px_rgba(205,127,50,0.25)]",
    cardBg: "bg-[#0c0d14]/95",
    glowColor: "#cd7f32",
    tagline: "VIP BRONZE MEMBER",
    perks: [
      "Slot Rezervat pe servere",
      "Stil Nume Animat Bronze",
      "+500 Puncte lunare",
      "Insignă VIP Bronze"
    ]
  },
  gold: {
    label: "VIP GOLD",
    badgeClass: "userBanner userBanner--ForumsvipGold",
    color: "#f59e0b",
    accentGrad: "from-amber-500 via-orange-500 to-amber-300",
    cardBorder: "border-amber-500/70 shadow-[0_20px_50px_rgba(245,158,11,0.35)]",
    cardBg: "bg-[#0e1019]/95",
    glowColor: "#f59e0b",
    tagline: "VIP GOLD ROYAL MEMBER",
    perks: [
      "Toate avantajele Bronze",
      "Cadre Avatar Animate",
      "Arme Exclusive Loadout",
      "Multiplicator Puncte 2x",
      "Camera VIP Staff Chat"
    ]
  },
  diamond: {
    label: "DONATOR DIAMOND",
    badgeClass: "userBanner userBanner--royalBlue",
    color: "#00e5ff",
    accentGrad: "from-[#00e5ff] via-[#8b5cf6] to-[#ec4899]",
    cardBorder: "border-[#00e5ff]/80 shadow-[0_25px_60px_rgba(0,229,255,0.35)]",
    cardBg: "bg-[#090e1c]/95",
    glowColor: "#00e5ff",
    tagline: "PERMANENT DIAMOND BENEFACTOR",
    perks: [
      "Permanent VIP Lifetime",
      "Rol & Culoare Discord",
      "Imunitate Server CS",
      "Case Openings Gratuite",
      "Insignă Donator Diamond"
    ]
  },
  founder: {
    label: "FOUNDER VIP",
    badgeClass: "userBanner userBanner--yellow",
    color: "#ffd700",
    accentGrad: "from-amber-400 via-yellow-300 to-amber-500",
    cardBorder: "border-yellow-400/80 shadow-[0_25px_60px_rgba(255,215,0,0.4)]",
    cardBg: "bg-[#0f0e0a]/95",
    glowColor: "#ffd700",
    tagline: "COMMUNITY FOUNDER",
    perks: [
      "Statut Fondator Comunitar",
      "Toate Pachetele VIP Deblocate",
      "Acces Nemărginit Loadout & Skins"
    ]
  },
  none: {
    label: "MEMBRU STANDARD",
    badgeClass: "",
    color: "#71717a",
    accentGrad: "from-zinc-700 via-zinc-500 to-zinc-700",
    cardBorder: "border-white/10 shadow-xl",
    cardBg: "bg-[#0b0e14]/90",
    glowColor: "#71717a",
    tagline: "COMMUNITY MEMBER",
    perks: []
  }
};

const DEFAULT_WIDGETS = ["about", "stats", "trophies", "posts", "topics"];

const THEME_MAP = {
  default: {
    className: "mockup--default",
    border: "border-white/10",
    grad: "from-zinc-600 via-zinc-400 to-zinc-600",
    tagline: "COMMUNITY MEMBER",
    glow: "rgba(255,255,255,0.05)",
    color: "#71717a"
  },
  bronze_identity: {
    className: "mockup--bronzeidentity",
    border: "border-[#cd7f32]/70",
    grad: "from-[#cd7f32] via-[#e5a65d] to-[#cd7f32]",
    tagline: "BRONZE WARRIOR",
    glow: "rgba(205,127,50,0.35)",
    color: "#cd7f32"
  },
  live_identity: {
    className: "mockup--liveidentity1",
    border: "border-amber-500/70",
    grad: "from-amber-500 via-orange-500 to-amber-300",
    tagline: "LIVE OBSIDIAN IDENTITY",
    glow: "rgba(245,158,11,0.4)",
    color: "#f59e0b"
  },
  arctic_identity: {
    className: "mockup--arcticcyber",
    border: "border-cyan-400/70",
    grad: "from-cyan-400 via-blue-500 to-teal-300",
    tagline: "ARCTIC CYBERPUNK",
    glow: "rgba(6,182,212,0.4)",
    color: "#00e5ff"
  },
  diamond_identity: {
    className: "mockup--diamondcyber",
    border: "border-[#8b5cf6]/80",
    grad: "from-[#8b5cf6] via-[#00e5ff] to-[#ec4899]",
    tagline: "HOLOGRAPHIC DIAMOND",
    glow: "rgba(139,92,246,0.45)",
    color: "#8b5cf6"
  },
  founder_identity: {
    className: "mockup--foundersupreme",
    border: "border-yellow-400/90",
    grad: "from-yellow-400 via-amber-300 to-yellow-500",
    tagline: "FOUNDER SUPREME",
    glow: "rgba(255,215,0,0.5)",
    color: "#ffd700"
  }
};

export default function Profile() {
  const { username } = useParams();
  const { user, realUser, isStaff, viewMode, setViewMode, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [widgets, setWidgets] = useState(DEFAULT_WIDGETS);
  const [reorderMode, setReorderMode] = useState(false);
  const [activeTab, setActiveTab] = useState("profile_posts");
  const [statusText, setStatusText] = useState("");
  const [statusPosts, setStatusPosts] = useState([]);
  const [following, setFollowing] = useState(false);
  const [ignored, setIgnored] = useState(false);
  const [findOpen, setFindOpen] = useState(false);
  const avatarFile = useRef(null);
  const bannerFile = useRef(null);
  const findRef = useRef(null);

  const load = () => {
    api.get(`/users/${username}`)
      .then((r) => {
        setData(r.data);
        const w = r.data.user?.profile_widgets;
        if (w && w.length) {
          const merged = [...new Set([...w, ...DEFAULT_WIDGETS])];
          setWidgets(merged);
        } else {
          setWidgets(DEFAULT_WIDGETS);
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error("Profilul nu a putut fi încărcat sau utilizatorul nu există.");
        setData({ error: true });
      });
  };

  useEffect(() => { load(); }, [username]);

  useEffect(() => {
    if (user && data?.user) {
      setFollowing(user.following?.includes(data.user.id) || false);
      setIgnored(user.ignored_users?.includes(data.user.id) || false);
    }
  }, [user, data]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (findRef.current && !findRef.current.contains(e.target)) {
        setFindOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (params.get("edit") === "1" && data && user?.username === data.user.username) {
      setForm(data.user);
      setEditing(true);
      params.delete("edit");
      setParams(params, { replace: true });
    }
  }, [data, user]);

  if (!data) return <div className="text-zinc-500 text-center py-20 tf-mono">Loading profile...</div>;
  if (data.error) return <div className="text-zinc-500 text-center py-20 tf-mono">Profilul nu a fost găsit.</div>;

  const u = data.user;
  const isOwn = user && user.username === u.username;
  const memberNumber = data.member_number || 0;

  const loadout = (isOwn && user?.loadout) ? { ...(u.loadout || {}), ...user.loadout } : (u.loadout || {});
  const rawTier = (u.vip_tier && u.vip_tier !== "none") 
    ? u.vip_tier.toLowerCase() 
    : (u.is_vip ? "gold" : "none");
  const tierConfig = VIP_TIER_CONFIG[rawTier] || VIP_TIER_CONFIG.none;
  const isVip = rawTier !== "none" || Boolean(u.is_vip);

  const themeId = loadout.profile_theme || u.profile_theme || (rawTier === "bronze" ? "bronze_identity" : rawTier === "diamond" ? "diamond_identity" : rawTier === "founder" ? "founder_identity" : isVip ? "live_identity" : "default");
  const activeTheme = THEME_MAP[themeId] || THEME_MAP.default;

  const selectedBadgeObj = BANNER_BADGES.find((b) => b.id === loadout.banner_badge) ||
    BANNER_BADGES.find((b) => b.id === u.vip_tier) ||
    (isVip ? BANNER_BADGES.find((b) => b.id === "ForumsvipGold") : null);

  const selectedWeaponObj = WEAPONS.find((w) => w.name === loadout.favorite_weapon || w.id === loadout.favorite_weapon);

  const bgImg = u.banner_url || ((process.env.PUBLIC_URL || "/forum") + "/banners/default_profile_banner.png");

  const uploadImage = async (file, kind) => {
    if (!file) return null;
    if (!file.type.startsWith("image/")) { toast.error("Doar imagini permise"); return null; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return null; }
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post(`/upload?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
  };

  const handleAvatarUpload = async (e) => {
    const url = await uploadImage(e.target.files?.[0], "avatar");
    if (url) {
      await api.patch("/users/me", { avatar_url: url });
      await refresh();
      load();
      toast.success("Avatar actualizat");
    }
    e.target.value = "";
  };

  const handleBannerUpload = async (e) => {
    const url = await uploadImage(e.target.files?.[0], "banner");
    if (url) {
      await api.patch("/users/me", { banner_url: url });
      await refresh();
      load();
      toast.success("Banner actualizat");
    }
    e.target.value = "";
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const patch = { ...form };
    delete patch.id; delete patch._id; delete patch.password_hash;
    await api.patch("/users/me", patch);
    toast.success("Profil actualizat");
    setEditing(false);
    await refresh();
    load();
  };

  const handleStatusPost = (e) => {
    e.preventDefault();
    if (!statusText.trim()) return;
    const newPost = {
      id: Date.now(),
      author: user?.username || "Guest",
      text: statusText,
      created_at: new Date().toISOString()
    };
    setStatusPosts([newPost, ...statusPosts]);
    setStatusText("");
    toast.success("Mesaj publicat pe profil");
  };

  const saveWidgetsOrder = async (newOrder) => {
    setWidgets(newOrder);
    if (isOwn) {
      try { await api.patch("/users/me", { profile_widgets: newOrder }); } catch {}
    }
  };

  // Background video / canvas animation extraction
  const bgType = loadout.background_video || "cosmic_nebula";
  const customBgUrl = loadout.custom_bg_video || "";

  // Video song ID extraction
  const songUrl = loadout.profile_song || "";
  const ytMatch = songUrl.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
  const songVideoId = ytMatch ? ytMatch[1] : (songUrl && songUrl.length === 11 ? songUrl : null);

  return (
    <div
      className={`min-h-screen relative text-zinc-200 ${activeTheme.className} overflow-hidden`}
      style={{
        background: (bgType !== "none" || customBgUrl)
          ? "#05070b" 
          : `radial-gradient(ellipse at 50% 30%, rgba(10,12,18,0.7) 0%, rgba(5,7,11,0.95) 100%), url(${bgImg}) center/cover fixed no-repeat`
      }}
      data-testid="profile-page"
    >
      {/* Dynamic Animated Canvas & Video Background */}
      {isVip && bgType !== "none" && (
        <AnimatedBackground type={bgType} customUrl={customBgUrl} />
      )}

      {/* Darkened Vignette Overlay */}
      <div className="fixed inset-0 bg-gradient-to-b from-black/75 via-black/45 to-black/80 pointer-events-none z-0" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 py-6">

        {/* 1. TOP BREADCRUMB */}
        <div className="flex items-center justify-between mb-4">
          <nav className="flex items-center gap-2 text-xs text-zinc-400 font-semibold tracking-wider uppercase">
            <Link to="/forums" className="hover:text-amber-400 flex items-center gap-1">
              <Home className="w-3.5 h-3.5" /> Forums
            </Link>
            <span>/</span>
            <span className="text-zinc-200">{u.username}</span>
          </nav>

          {isOwn && (
            <div className="flex items-center gap-2">
              <Link
                to="/vip"
                className="px-3 py-1 text-xs font-bold uppercase rounded bg-amber-500/20 text-amber-400 border border-amber-500/40 hover:bg-amber-500 hover:text-black transition-all flex items-center gap-1 shadow-[0_0_12px_rgba(245,158,11,0.25)]"
              >
                <Crown className="w-3.5 h-3.5" /> VIP Loadout
              </Link>
              <button
                onClick={() => { setForm(u); setEditing(true); }}
                className="px-3 py-1 text-xs font-bold uppercase rounded bg-white/10 text-zinc-300 hover:bg-white/20 transition-all flex items-center gap-1"
              >
                <Edit className="w-3 h-3" /> Editează
              </button>
            </div>
          )}
        </div>

        {/* 2. MAIN 2-COLUMN GRID (Left Profile Card + Right YouTube Video widget) */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">

          {/* LEFT 3-COLUMN SPAN: FULL PROFILE CARD + FEED */}
          <div className="lg:col-span-3 space-y-6">

            {isVip ? (
            <div 
              className={`fullProfileMock ${activeTheme.className} relative ${tierConfig.cardBg} ${activeTheme.border} rounded-xl overflow-hidden backdrop-blur-md transition-all duration-300`}
              style={{
                boxShadow: isVip ? `0 20px 60px ${activeTheme.glow}` : "0 20px 50px rgba(0,0,0,0.8)"
              }}
            >
              
              {/* TOP ACCENT BAR */}
              <div className={`h-1.5 w-full bg-gradient-to-r ${activeTheme.grad} shadow-lg`} />

              {/* HEADER TOP-RIGHT META TAGS */}
              <div className="absolute top-3 right-4 flex items-center gap-3 z-20">
                <span 
                  className="text-[9px] font-black tracking-widest uppercase font-mono hidden sm:inline-block px-2 py-0.5 rounded border"
                  style={{ color: activeTheme.color, borderColor: `${activeTheme.color}40`, backgroundColor: `${activeTheme.color}15` }}
                >
                  {activeTheme.tagline}
                </span>
                <button className="px-2 py-0.5 text-[10px] font-bold uppercase border border-cyan-500/40 text-cyan-400 bg-cyan-950/40 rounded hover:bg-cyan-500 hover:text-black transition-all flex items-center gap-1">
                  <Flag className="w-2.5 h-2.5" /> REPORT
                </button>
              </div>

              {/* HEADER INNER CONTENT */}
              <div className="p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">

                  {/* LARGE AVATAR + FRAME */}
                  <div className="relative flex-shrink-0">
                    <div 
                      className="w-32 h-32 sm:w-36 sm:h-36 overflow-hidden relative shadow-2xl transition-all"
                    >
                      <Avatar user={u} size={144} />
                    </div>
                    {isOwn && (
                      <button
                        onClick={() => avatarFile.current?.click()}
                        className="absolute inset-0 bg-black/60 opacity-0 hover:opacity-100 flex items-center justify-center text-white transition-opacity"
                        title="Schimbă avatar"
                      >
                        <Camera className="w-6 h-6" />
                      </button>
                    )}
                    <input ref={avatarFile} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
                  </div>

                  {/* USER IDENTITY & STATS */}
                  <div className="flex-1 min-w-0 text-center sm:text-left">
                    
                    {/* USERNAME */}
                    <div className="flex items-center justify-center sm:justify-start gap-3 flex-wrap mb-2">
                      <Username user={u} className="text-3xl sm:text-4xl font-black font-['Rajdhani'] uppercase tracking-wider" asText />
                    </div>

                    {/* BANNER BADGE & VIP DURATION STATUS */}
                    <div className="mb-3 flex flex-col items-center sm:items-start gap-1.5">
                      {isVip ? (
                        <>
                          <div className="flex items-center gap-2">
                            {selectedBadgeObj && selectedBadgeObj.id !== "none" ? (
                              <span className={`userBanner ${selectedBadgeObj.className || tierConfig.badgeClass}`}>
                                <span className="userBanner-before"></span>
                                <strong>{selectedBadgeObj.text || selectedBadgeObj.label}</strong>
                                <span className="userBanner-after"></span>
                              </span>
                            ) : (
                              <span className={`userBanner ${tierConfig.badgeClass}`}>
                                <span className="userBanner-before"></span>
                                <strong>{tierConfig.label}</strong>
                                <span className="userBanner-after"></span>
                              </span>
                            )}
                          </div>

                          {/* VIP Duration Expiry Status */}
                          <div 
                            className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[10px] font-mono rounded border"
                            style={{ borderColor: `${tierConfig.color}40`, backgroundColor: `${tierConfig.color}15`, color: tierConfig.color }}
                          >
                            <Clock className="w-3 h-3" style={{ color: tierConfig.color }} />
                            <span>
                              {u.vip_expires_at || u.vip_until
                                ? `VIP Activ (${tierConfig.label}) · Expiră în ${formatDistanceToNow(new Date(u.vip_expires_at || u.vip_until))}` 
                                : `${tierConfig.label} · Activ Permanent`}
                            </span>
                            {u.vip_purchase_count ? (
                              <span className="border-l pl-1.5 ml-0.5 font-bold" style={{ borderColor: `${tierConfig.color}40` }}>
                                x{u.vip_purchase_count}
                              </span>
                            ) : null}
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="px-2.5 py-0.5 text-[10px] font-chakra font-black uppercase tracking-wider rounded bg-white/5 text-zinc-400 border border-white/10">
                            MEMBRU COMUNITATE
                          </span>
                        </div>
                      )}
                    </div>

                    {/* JOINED & LAST SEEN */}
                    <div className="text-[11px] font-mono text-zinc-400 space-y-0.5 mb-4">
                      <div>JOINED: <span className="text-zinc-200">{u.joined_at ? format(new Date(u.joined_at), "MMM d, yyyy") : "May 16, 2026"}</span></div>
                      <div>LAST SEEN: <span className="text-zinc-200">{u.last_seen ? formatDistanceToNow(new Date(u.last_seen)) + " ago" : "3 minutes ago · Viewing Profile"}</span></div>
                    </div>

                    {/* 4-COLUMN STATS BOX */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 border-y border-white/5 py-4 mb-4 text-center">
                      <div>
                        <div className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest">MESSAGES</div>
                        <div className="text-lg font-black text-white font-['Rajdhani']">{u.message_count || 3}</div>
                      </div>
                      <div>
                        <div className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest">REACTION SCORE</div>
                        <div className="text-lg font-black text-amber-400 font-['Rajdhani']">{u.reaction_score || 0}</div>
                      </div>
                      <div>
                        <div className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest">FAVORITE WEAPON</div>
                        <div className="text-sm font-black text-cyan-400 font-['Rajdhani'] flex items-center justify-center gap-1 mt-0.5">
                          {selectedWeaponObj ? (
                            <img src={selectedWeaponObj.iconUrl} alt={selectedWeaponObj.name} className="h-5 object-contain filter drop-shadow" />
                          ) : (
                            loadout.favorite_weapon || "M4A4"
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest">FORUM CREDITS</div>
                        <div className="text-lg font-black text-emerald-400 font-['Rajdhani']">{u.points || 896}</div>
                      </div>
                    </div>

                    {/* VIP PERKS SHOWCASE vs NON-VIP UPGRADE BANNER */}
                    {isVip && tierConfig.perks && tierConfig.perks.length > 0 ? (
                      <div 
                        className="p-3 rounded-lg border mb-5 space-y-1.5" 
                        style={{ borderColor: `${tierConfig.color}35`, backgroundColor: `${tierConfig.color}0a` }}
                      >
                        <div className="text-[10px] font-chakra font-black uppercase tracking-wider flex items-center gap-1.5" style={{ color: tierConfig.color }}>
                          <Sparkles className="w-3.5 h-3.5" />
                          <span>Beneficii Active VIP ({tierConfig.label})</span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {tierConfig.perks.map((p, idx) => (
                            <span 
                              key={idx} 
                              className="text-[10px] font-mono px-2 py-0.5 rounded border flex items-center gap-1.5"
                              style={{ borderColor: `${tierConfig.color}30`, backgroundColor: `${tierConfig.color}15`, color: "#f4f4f5" }}
                            >
                              <Check className="w-3 h-3" style={{ color: tierConfig.color }} />
                              <span>{p}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="p-3.5 rounded-lg border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent flex items-center justify-between gap-4 mb-5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400 shrink-0">
                            <Crown className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="font-chakra font-black text-xs uppercase tracking-wider text-white">
                              Upgrade la VIP & Deblochează Loadout-ul
                            </div>
                            <div className="text-[10px] font-mono text-zinc-400">
                              Rame animate, nume stilizate, arme exclusive și slot rezervat pe servere.
                            </div>
                          </div>
                        </div>
                        <Link
                          to="/donate?tab=vip"
                          className="px-3 py-1.5 rounded font-chakra font-black text-[11px] uppercase tracking-wider text-black bg-amber-400 hover:bg-amber-300 transition-all shrink-0 shadow-sm flex items-center gap-1"
                        >
                          <span>Devino VIP</span>
                          <ArrowUpRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    )}

                    {/* ACTION BUTTONS ROW */}
                    <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap">
                      {/* 0. VEZI CA MEMBRU SIMULATION BUTTON (For root & admin only) */}
                      {isStaff && (
                        <button
                          onClick={() => {
                            const nextMode = viewMode === "member" ? "admin" : "member";
                            setViewMode(nextMode);
                            toast.info(nextMode === "member" ? "Mod Membru activat (simulare fără privilegii staff)" : "Revenit la modul Administrator");
                          }}
                          className={`px-3 py-1.5 rounded border text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-1.5 cursor-pointer ${
                            viewMode === "member" 
                              ? "bg-amber-500 text-black border-amber-400 font-black shadow-[0_0_12px_rgba(245,158,11,0.5)]" 
                              : "border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20"
                          }`}
                          title="Simulează cum vede un membru obișnuit profilul și forumul"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span>{viewMode === "member" ? "MOD MEMBRU ACTIV" : "VEZI CA MEMBRU"}</span>
                        </button>
                      )}

                      {/* 1. FOLLOW BUTTON */}
                      <button
                        onClick={async () => {
                          if (!user) { toast.error("Trebuie să fii autentificat pentru a urmări un utilizator."); return; }
                          if (isOwn) { toast.error("Nu te poți urmări pe tine însuți."); return; }
                          try {
                            const res = await api.post(`/users/${u.username}/follow`);
                            setFollowing(res.data.following);
                            if (res.data.following) toast.success(`Urmărești acum pe ${u.username}`);
                            else toast.info(`Nu mai urmărești pe ${u.username}`);
                            refresh();
                          } catch (err) { toast.error(err.response?.data?.detail || "Eroare la urmărire"); }
                        }}
                        className={`px-3 py-1.5 rounded border text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                          following 
                            ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-300 shadow-[0_0_10px_rgba(16,185,129,0.2)]" 
                            : "border-white/15 bg-white/5 hover:bg-white/10 text-zinc-300 hover:text-white"
                        }`}
                      >
                        {following ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <UserPlus className="w-3.5 h-3.5 text-amber-400" />}
                        {following ? "FOLLOWING" : "FOLLOW"}
                      </button>

                      {/* 2. IGNORE BUTTON */}
                      <button
                        onClick={async () => {
                          if (!user) { toast.error("Trebuie să fii autentificat pentru a ignora un utilizator."); return; }
                          if (isOwn) { toast.error("Nu te poți ignora pe tine însuți."); return; }
                          try {
                            const res = await api.post(`/users/${u.username}/ignore`);
                            setIgnored(res.data.ignored);
                            if (res.data.ignored) toast.warning(`Ai adăugat pe ${u.username} la utilizatori ignorați`);
                            else toast.info(`Ai scos pe ${u.username} de la ignorare`);
                            refresh();
                          } catch (err) { toast.error(err.response?.data?.detail || "Eroare la ignorare"); }
                        }}
                        className={`px-3 py-1.5 rounded border text-xs font-bold uppercase tracking-wider transition-all ${
                          ignored 
                            ? "bg-red-500/20 border-red-500/50 text-red-300 shadow-[0_0_10px_rgba(239,68,68,0.2)]" 
                            : "border-white/15 bg-white/5 hover:bg-white/10 text-zinc-300 hover:text-white"
                        }`}
                      >
                        {ignored ? "IGNORED" : "IGNORE"}
                      </button>

                      {/* 3. SEND DIRECT MESSAGE BUTTON */}
                      <Link
                        to={`/messages?dm=${u.username}`}
                        className="px-3 py-1.5 rounded border border-cyan-500/40 bg-cyan-950/20 hover:bg-cyan-500/30 text-xs font-bold uppercase tracking-wider text-cyan-300 hover:text-cyan-200 transition-all flex items-center gap-1.5"
                      >
                        <Send className="w-3.5 h-3.5 text-cyan-400" /> SEND DIRECT MESSAGE
                      </Link>

                      {/* 4. FIND DROPDOWN BUTTON */}
                      <div className="relative" ref={findRef}>
                        <button
                          onClick={() => setFindOpen((v) => !v)}
                          className="px-3 py-1.5 rounded border border-white/15 bg-white/5 hover:bg-white/10 text-xs font-bold uppercase tracking-wider text-zinc-300 hover:text-white transition-all flex items-center gap-1"
                        >
                          FIND <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${findOpen ? "rotate-180 text-amber-400" : ""}`} />
                        </button>
                        {findOpen && (
                          <div className="absolute left-0 sm:left-auto sm:right-0 mt-1.5 w-60 rounded-xl bg-[#0c1017] border border-white/15 shadow-[0_15px_40px_rgba(0,0,0,0.9)] py-1.5 z-50 text-left tf-fade-in">
                            <button
                              onClick={() => { setActiveTab("postings"); setFindOpen(false); toast.info(`Afișez postările lui ${u.username}`); }}
                              className="w-full px-3.5 py-2 text-xs text-zinc-300 hover:text-white hover:bg-white/10 flex items-center gap-2.5 transition-colors text-left"
                            >
                              <MessageSquare className="w-3.5 h-3.5 text-amber-400 shrink-0" /> Toate postările lui {u.username}
                            </button>
                            <button
                              onClick={() => { setActiveTab("postings"); setFindOpen(false); toast.info(`Afișez topicurile lui ${u.username}`); }}
                              className="w-full px-3.5 py-2 text-xs text-zinc-300 hover:text-white hover:bg-white/10 flex items-center gap-2.5 transition-colors text-left"
                            >
                              <Crosshair className="w-3.5 h-3.5 text-cyan-400 shrink-0" /> Toate topicurile lui {u.username}
                            </button>
                            <button
                              onClick={() => { setActiveTab("profile_posts"); setFindOpen(false); }}
                              className="w-full px-3.5 py-2 text-xs text-zinc-300 hover:text-white hover:bg-white/10 flex items-center gap-2.5 transition-colors text-left border-t border-white/5 mt-1 pt-1.5"
                            >
                              <MessageCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> Mesaje pe profil
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                  </div>
                </div>

                {/* PERSONAL QUOTE BOX */}
                {loadout.personal_quote && (
                  <div className="mt-6 p-4 rounded-lg border border-amber-500/40 bg-amber-950/20 text-center sm:text-left shadow-[inset_0_0_20px_rgba(245,158,11,0.08)]">
                    <p className="text-amber-300 font-semibold italic text-sm sm:text-base m-0 font-['Rajdhani']">
                      "{loadout.personal_quote}"
                    </p>
                    <span className="text-[10px] font-black text-amber-500 uppercase tracking-widest block mt-1">
                      — {u.username}
                    </span>
                  </div>
                )}

              </div>

              {/* BOTTOM-RIGHT CYAN CORNER BRACKET */}
              <div className="absolute bottom-2 right-2 w-4 h-4 border-r-2 border-b-2 border-cyan-400 pointer-events-none" />
              </div>
            ) : (
              <StandardProfileHeader u={u} isOwn={isOwn} setForm={setForm} setEditing={setEditing} />
            )}

            {/* EDIT FORM */}
            {editing && (
              <form onSubmit={handleSave} className="tf-surface-elev tf-clip-card p-6 space-y-3" data-testid="profile-edit-form">
                <div className="flex items-center justify-between">
                  <h3 className="tf-section-title">Editează profilul</h3>
                  <button type="button" onClick={() => setEditing(false)} className="text-zinc-400 hover:text-white" data-testid="edit-close">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <Field label="Bio" value={form.bio || ""} onChange={(v) => setForm({ ...form, bio: v })} textarea testid="edit-bio" />
                  <Field label="Locație / Țară" value={form.location || ""} onChange={(v) => setForm({ ...form, location: v })} testid="edit-location" />
                  <Field label="Vârstă" type="number" value={form.age || ""} onChange={(v) => setForm({ ...form, age: parseInt(v) || null })} testid="edit-age" />
                  <Field label="Sursă" value={form.referral_source || ""} onChange={(v) => setForm({ ...form, referral_source: v })} testid="edit-referral" />
                  <Field label="Semnătură" value={form.signature || ""} onChange={(v) => setForm({ ...form, signature: v })} testid="edit-signature" />
                  <Field label="Joc favorit" value={form.favorite_game || ""} onChange={(v) => setForm({ ...form, favorite_game: v })} testid="edit-fav-game" />
                  <Field label="Steam ID" value={form.steam_id || ""} onChange={(v) => setForm({ ...form, steam_id: v })} testid="edit-steam-id" />
                  <Field label="Discord Username" value={form.discord_username || ""} onChange={(v) => setForm({ ...form, discord_username: v })} testid="edit-discord" />
                  <Field label="CS2 Rank" value={form.cs2_rank || ""} onChange={(v) => setForm({ ...form, cs2_rank: v })} testid="edit-cs2-rank" />
                </div>
                <div className="flex gap-2 pt-2">
                  <button className="tf-btn-primary tf-clip-btn flex items-center gap-1" type="submit" data-testid="edit-save">
                    <Save className="w-3.5 h-3.5" /> Salvează
                  </button>
                  <button className="tf-btn-secondary tf-clip-btn" type="button" onClick={() => setEditing(false)}>Anulează</button>
                </div>
              </form>
            )}

            {/* NAVIGATION TABS */}
            <div className="flex items-center gap-1 border-b border-white/10 bg-black/40 px-3 py-1 rounded-t-lg overflow-x-auto scrollbar-hide">
              {[
                { id: "profile_posts", label: "PROFILE POSTS" },
                { id: "match_history", label: "MATCH HISTORY" },
                { id: "latest_activity", label: "LATEST ACTIVITY" },
                { id: "postings", label: "POSTINGS" },
                { id: "about", label: "ABOUT" }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 text-xs font-black tracking-wider uppercase transition-all rounded-t relative ${
                    activeTab === tab.id
                      ? "text-amber-400 bg-amber-500/10 border-b-2 border-amber-500 font-bold"
                      : "text-zinc-400 hover:text-zinc-200"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* TAB CONTENT: PROFILE POSTS */}
            {activeTab === "profile_posts" && (
              <div className="space-y-4">
                {/* STATUS UPDATE INPUT */}
                <form onSubmit={handleStatusPost} className="flex gap-3 bg-[#0a0e17]/80 border border-white/10 p-3 rounded-lg backdrop-blur-md">
                  <div className="w-9 h-9 rounded-lg bg-amber-500 flex items-center justify-center font-bold text-black text-sm flex-shrink-0">
                    {user?.username?.[0]?.toUpperCase() || "U"}
                  </div>
                  <input
                    type="text"
                    value={statusText}
                    onChange={(e) => setStatusText(e.target.value)}
                    placeholder="Write something..."
                    className="flex-1 bg-transparent border-none text-sm text-white placeholder-zinc-500 focus:outline-none"
                  />
                  <button
                    type="submit"
                    disabled={!statusText.trim()}
                    className="px-3 py-1 rounded bg-amber-500 text-black font-black text-xs uppercase disabled:opacity-30 disabled:cursor-not-allowed hover:bg-amber-400 transition-all"
                  >
                    Post
                  </button>
                </form>

                {/* GAME STATISTICS CS2 CARD — INDUNGI NETWORK */}
                <div className="bg-[#090d14]/80 border border-white/10 rounded-xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.5)]">
                  <div className="px-4 py-2.5 bg-black/40 border-b border-white/5 text-[10px] font-black tracking-widest text-zinc-400 uppercase font-mono flex items-center justify-between">
                    <span>GAME STATISTICS // INDUNGI ROMANIA</span>
                    <span className="text-amber-400/80">LIVE STEAM STATUS</span>
                  </div>
                  <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      {u.steam_avatar ? (
                        <img src={u.steam_avatar} alt="Steam" className="w-12 h-12 rounded-lg object-cover border border-cyan-500/40 shadow-[0_0_10px_rgba(0,255,255,0.2)]" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-[#171a21] border border-cyan-500/30 flex items-center justify-center text-cyan-400 font-black text-2xl shadow-[0_0_12px_rgba(0,255,255,0.15)]">
                          <i className="fa-brands fa-steam"></i>
                        </div>
                      )}
                      <div>
                        <div className="text-sm font-bold text-white flex items-center gap-2">
                          {u.steam_name || u.username}
                          {u.steam_id && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-cyan-950/60 border border-cyan-500/30 text-cyan-300">LINKED</span>}
                        </div>
                        <div className="text-[11px] font-mono text-zinc-400">
                          {u.steam_id || u.steam_id64 ? (
                            <span className="text-zinc-300 font-semibold">{u.steam_id || u.steam_id64}</span>
                          ) : isOwn ? (
                            <button onClick={() => { setForm(u); setEditing(true); }} className="text-amber-400 hover:underline flex items-center gap-1">
                              <Edit className="w-3 h-3" /> Conectează Steam ID
                            </button>
                          ) : (
                            <span className="text-zinc-500 italic">Steam neasociat</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {(u.steam_id || u.steam_id64) && (
                        <>
                          {u.vac_banned ? (
                            <span className="px-2.5 py-1 text-[10px] font-black uppercase rounded bg-red-950/60 border border-red-500/40 text-red-400">
                              VAC BANNED
                            </span>
                          ) : (
                            <span className="px-2.5 py-1 text-[10px] font-black uppercase rounded bg-emerald-950/60 border border-emerald-500/40 text-emerald-400 flex items-center gap-1">
                              <Check className="w-3 h-3" /> VAC Clean
                            </span>
                          )}
                          <span className="px-2.5 py-1 text-[10px] font-black uppercase rounded bg-emerald-500 text-black font-mono">
                            ACTIVE
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 border-t border-white/5 px-4 py-2.5 text-center bg-black/20 text-xs font-mono">
                    <div>
                      <span className="text-[10px] text-zinc-500 uppercase block">LAST SERVER</span>
                      <span className="text-amber-400 font-bold truncate block">
                        {u.last_server || "—"}
                      </span>
                    </div>
                    <div>
                      <span className="text-[10px] text-zinc-500 uppercase block">PING</span>
                      <span className="text-emerald-400 font-bold block">{u.last_ping ? `${u.last_ping} ms` : "18 ms"}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-zinc-500 uppercase block">LAST ACTIVE</span>
                      <span className="text-zinc-300 block">
                        {u.last_game_active ? format(new Date(u.last_game_active), "M/d/yy h:mm a") : (u.last_seen ? format(new Date(u.last_seen), "M/d/yy h:mm a") : format(new Date(), "M/d/yy h:mm a"))}
                      </span>
                    </div>
                  </div>
                </div>

                {/* POSTS LIST */}
                {statusPosts.length > 0 ? (
                  statusPosts.map((post) => (
                    <div key={post.id} className="relative bg-transparent border-b border-white/5 py-4 pl-4 space-y-2 group hover:bg-white/5 transition-colors">
                      <div className="absolute left-0 top-0 bottom-0 w-[2px] opacity-0 group-hover:opacity-100 transition-opacity bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.5)]" />
                      <div className="flex items-center justify-between text-xs text-zinc-500 font-mono">
                        <span className="font-bold text-amber-500">{post.author}</span>
                        <span>{formatDistanceToNow(new Date(post.created_at))} ago</span>
                      </div>
                      <div
                        className="text-zinc-300 text-sm prose prose-invert max-w-none"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(post.text || "") }}
                      />
                    </div>
                  ))
                ) : (
                  <div className="py-8 text-center text-zinc-600 text-sm font-mono uppercase tracking-widest border border-dashed border-white/10 rounded-lg">
                    There are no messages on {u.username}'s profile yet.
                  </div>
                )}
              </div>
            )}

            {/* TAB CONTENT: MATCH HISTORY */}
            {activeTab === "match_history" && (
              <div className="bg-[#12161f] border border-white/5 rounded-xl p-8 sm:p-12 text-center shadow-lg">
                <div className="w-16 h-16 rounded-2xl bg-zinc-900 border border-white/5 mx-auto flex items-center justify-center mb-4">
                  <Gamepad2 className="w-8 h-8 text-zinc-600" />
                </div>
                <h3 className="text-lg font-black tracking-wide text-zinc-300">Nu s-au găsit meciuri</h3>
                <p className="text-sm text-zinc-500 mt-2 max-w-sm mx-auto">
                  Acest utilizator nu a jucat încă meciuri de tip MIX în comunitate. Meciurile vor fi afișate aici odată ce sunt sincronizate.
                </p>
                {isOwn && (
                  <button className="mt-6 px-5 py-2.5 rounded-lg border border-cyan-500/40 bg-cyan-950/20 hover:bg-cyan-500 hover:text-black text-sm font-bold uppercase tracking-wider text-cyan-300 transition-all flex items-center justify-center gap-2 mx-auto">
                    <Gamepad2 className="w-4 h-4" /> Sync Steam Matches
                  </button>
                )}
              </div>
            )}

            {/* TAB CONTENT: ABOUT */}
            {activeTab === "about" && (
              <DraggableWidgets
                widgets={widgets}
                reorderMode={reorderMode && isOwn}
                onReorder={saveWidgetsOrder}
                renderWidget={(id) => <WidgetContent id={id} user={u} data={data} />}
              />
            )}

            {/* TAB CONTENT: POSTINGS */}
            {activeTab === "postings" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <PostsWidget posts={data.recent_posts} />
                <TopicsWidget topics={data.recent_topics} />
              </div>
            )}

            {/* TAB CONTENT: LATEST ACTIVITY */}
            {activeTab === "latest_activity" && (
              <div className="bg-[#12161f] border border-white/5 rounded-xl p-8 sm:p-12 text-center shadow-lg">
                <div className="w-16 h-16 rounded-2xl bg-zinc-900 border border-white/5 mx-auto flex items-center justify-center mb-4">
                  <Clock className="w-8 h-8 text-zinc-600" />
                </div>
                <h3 className="text-lg font-black tracking-wide text-zinc-300">Nu există activitate</h3>
                <p className="text-sm text-zinc-500 mt-2 max-w-sm mx-auto">
                  Acest utilizator nu are încă evenimente recente care să poată fi afișate pe timeline-ul public.
                </p>
              </div>
            )}


          </div>

          {/* RIGHT 1-COLUMN SPAN: YOUTUBE VIDEO WIDGET */}
          <div className="space-y-6">

            {isVip && (
            <div className="bg-[#090d14]/90 border border-cyan-500/30 rounded-xl overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,0.8)] backdrop-blur-md">
              <div className="px-4 py-3 bg-cyan-950/40 border-b border-cyan-500/20 flex items-center gap-2">
                <Play className="w-3.5 h-3.5 text-cyan-400" />
                <h3 className="text-xs font-black tracking-widest text-cyan-400 uppercase font-mono m-0">
                  YOUTUBE VIDEO
                </h3>
              </div>
              <div className="p-3">
                {songVideoId ? (
                  <div className="aspect-video w-full rounded-lg overflow-hidden border border-white/10 bg-black">
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${songVideoId}?autoplay=${loadout.song_autoplay ? 1 : 0}`}
                      title="Profile Song"
                      className="w-full h-full"
                      allow="autoplay; encrypted-media"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <div className="aspect-video w-full rounded-lg border border-white/10 bg-zinc-950 flex flex-col items-center justify-center text-center p-4">
                    <Music className="w-8 h-8 text-zinc-600 mb-2" />
                    <span className="text-[11px] font-mono text-zinc-500 uppercase">No video configured</span>
                  </div>
                )}
              </div>
            </div>
            )}
            
            {/* COMMUNITY RANKS WIDGET */}
            <div className="bg-[#090d14]/90 border border-white/10 rounded-xl p-4 space-y-3 backdrop-blur-md">
              <div className="text-xs font-black tracking-widest text-zinc-400 uppercase font-mono border-b border-white/5 pb-2">
                PLAYER BADGES
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedBadgeObj && selectedBadgeObj.id !== "none" && (
                  <span className={`userBanner ${selectedBadgeObj.className || "userBanner--orange"}`}>
                    <span className="userBanner-before"></span>
                    <strong>{selectedBadgeObj.text || selectedBadgeObj.label}</strong>
                    <span className="userBanner-after"></span>
                  </span>
                )}
                {u.role === "admin" && (
                  <span className="px-2 py-0.5 text-[10px] font-black uppercase rounded bg-red-600 text-white">
                    Admin
                  </span>
                )}
                {u.role === "root" && (
                  <span className="px-2 py-0.5 text-[10px] font-black uppercase rounded bg-purple-600 text-white">
                    Root
                  </span>
                )}
              </div>
            </div>

          </div>

        </div>

      </div>
    </div>
  );
}

function Field({ label, value, onChange, textarea, testid }) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider font-mono">{label}</label>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-black/50 border border-white/10 rounded p-2 text-sm text-white focus:border-amber-500 focus:outline-none"
          rows={3}
          data-testid={testid}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-black/50 border border-white/10 rounded p-2 text-sm text-white focus:border-amber-500 focus:outline-none"
          data-testid={testid}
        />
      )}
    </div>
  );
}

function DraggableWidgets({ widgets, reorderMode, onReorder, renderWidget }) {
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = widgets.indexOf(active.id);
    const newIdx = widgets.indexOf(over.id);
    onReorder(arrayMove(widgets, oldIdx, newIdx));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={widgets} strategy={verticalListSortingStrategy}>
        <div className="space-y-4" data-testid="profile-widgets">
          {widgets.map((id) => (
            <SortableWidget key={id} id={id} reorderMode={reorderMode}>
              {renderWidget(id)}
            </SortableWidget>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableWidget({ id, reorderMode, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !reorderMode });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className={`relative ${reorderMode ? "ring-1 ring-dashed ring-[var(--tf-primary)]/60" : ""}`} data-testid={`widget-${id}`}>
      {reorderMode && (
        <div
          {...attributes}
          {...listeners}
          className="absolute -left-3 top-3 w-7 h-7 flex items-center justify-center bg-[var(--tf-primary)] text-black rounded cursor-grab active:cursor-grabbing z-10"
          data-testid={`drag-handle-${id}`}
        >
          <GripVertical className="w-4 h-4" />
        </div>
      )}
      {children}
    </div>
  );
}

function WidgetContent({ id, user: u, data }) {
  if (id === "about") return <AboutWidget user={u} />;
  if (id === "stats") return <StatsWidget user={u} />;
  if (id === "trophies") return <TrophiesWidget user={u} />;
  if (id === "posts") return <PostsWidget posts={data.recent_posts} />;
  if (id === "topics") return <TopicsWidget topics={data.recent_topics} />;
  return null;
}

function WidgetCard({ title, icon: Icon, children, testid }) {
  return (
    <section className="bg-transparent border-t border-white/5 pt-4 mt-6" data-testid={testid}>
      <header className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="w-4 h-4 text-[var(--tf-primary)]" />}
        <h3 className="text-sm font-black tracking-widest text-white uppercase font-mono">{title}</h3>
      </header>
      <div>{children}</div>
    </section>
  );
}

function AboutCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-transparent border-b border-white/5 pb-2 flex items-center gap-4 hover:border-[var(--tf-primary)]/30 transition-colors">
      <div className="w-8 h-8 shrink-0 rounded flex items-center justify-center bg-transparent text-[var(--tf-primary)]">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono">{label}</div>
        <div className="text-sm font-bold text-white mt-0.5 truncate">{value}</div>
      </div>
    </div>
  );
}

function AboutWidget({ user: u }) {
  return (
    <WidgetCard title="Despre Utilizator" icon={User} testid="widget-about-card">
      <div className="bg-[#12161f] border border-white/5 rounded-xl p-6 sm:p-8">
        
        {u.bio && (
          <div className="mb-8">
            <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-3">Biografie</h4>
            <div className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap bg-black/20 p-4 rounded-lg border border-white/5">
              {u.bio}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-3">Informații Generale</h4>
            <AboutCard icon={MapPin} label="Locație" value={u.location || "Nespecificat"} />
            <AboutCard icon={User} label="Vârstă" value={u.age ? `${u.age} ani` : "Nespecificat"} />
            <AboutCard icon={Calendar} label="Înregistrat" value={u.joined_at ? format(new Date(u.joined_at), "PPP") : "Nespecificat"} />
            <AboutCard icon={Clock} label="Ultima oară activ" value={u.last_seen ? formatDistanceToNow(new Date(u.last_seen), { addSuffix: true }) : "Nespecificat"} />
          </div>
          
          <div className="space-y-4">
            <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-3">Gaming & Contact</h4>
            <AboutCard icon={Gamepad2} label="Joc Favorit" value={u.favorite_game?.toUpperCase() || "Nespecificat"} />
            <AboutCard icon={Monitor} label="Steam ID" value={u.steam_id || "Neconectat"} />
            <AboutCard icon={MessageCircle} label="Discord" value={u.discord_username || "Neconectat"} />
            <AboutCard icon={Award} label="VIP" value={u.vip_tier && u.vip_tier !== 'none' ? u.vip_tier.toUpperCase() : "Membru Standard"} />
          </div>
        </div>

        {u.signature && (
          <div className="mt-8 pt-6 border-t border-white/10">
            <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-3 flex items-center gap-2">
              <Edit3 className="w-4 h-4 text-amber-500" /> Semnătură
            </h4>
            <div className="text-zinc-400 italic text-sm border-l-2 border-amber-500/50 pl-4 py-1">
              {u.signature}
            </div>
          </div>
        )}

      </div>
    </WidgetCard>
  );
}

function StatsWidget({ user: u }) {
  return (
    <WidgetCard title="Statistici" icon={Trophy} testid="widget-stats-card">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="border-b border-white/5 pb-3 text-center">
          <div className="text-2xl font-black text-white font-['Rajdhani']">{u.message_count || 0}</div>
          <div className="text-[10px] text-zinc-500 uppercase font-mono mt-1">Postări</div>
        </div>
        <div className="border-b border-white/5 pb-3 text-center">
          <div className="text-2xl font-black text-[var(--tf-primary)] font-['Rajdhani']">{u.reaction_score || 0}</div>
          <div className="text-[10px] text-zinc-500 uppercase font-mono mt-1">Reacții</div>
        </div>
        <div className="border-b border-white/5 pb-3 text-center">
          <div className="text-2xl font-black text-cyan-400 font-['Rajdhani']">{u.points || 0}</div>
          <div className="text-[10px] text-zinc-500 uppercase font-mono mt-1">Puncte</div>
        </div>
        <div className="border-b border-white/5 pb-3 text-center">
          <div className="text-2xl font-black text-emerald-400 font-['Rajdhani']">{u.topic_count || 0}</div>
          <div className="text-[10px] text-zinc-500 uppercase font-mono mt-1">Topicuri</div>
        </div>
      </div>
    </WidgetCard>
  );
}

function TrophiesWidget({ user: u }) {
  const userTrophies = u.trophies || ["first_post", "vip_member"];
  return (
    <WidgetCard title="Trofee & Realizări" icon={Award} testid="widget-trophies-card">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {userTrophies.map((tKey) => {
          const trophy = TROPHY_MAP[tKey] || { label: tKey, icon: "🏆" };
          return (
            <div key={tKey} className="pb-2 border-b border-white/5 flex items-center gap-3">
              <span className="text-2xl">{trophy.icon}</span>
              <span className="text-xs font-bold text-zinc-400">{trophy.label}</span>
            </div>
          );
        })}
      </div>
    </WidgetCard>
  );
}

function PostsWidget({ posts = [] }) {
  if (!posts.length) {
    return (
      <WidgetCard title="Activități recente" icon={MessageSquare} testid="widget-posts-card">
        <div className="text-xs text-zinc-500 italic p-2">Nu există activități recente.</div>
      </WidgetCard>
    );
  }
  return (
    <WidgetCard title="Activități recente" icon={MessageSquare} testid="widget-posts-card">
      <div className="space-y-4">
        {posts.map((p) => (
          <div key={p.id} className="relative pl-4 border-b border-white/5 pb-3 group">
            <div className="absolute left-0 top-0 bottom-0 w-[2px] opacity-0 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: 'var(--tf-primary)', boxShadow: '0 0 10px var(--tf-glow)' }} />
            <Link to={`/topic/${p.topic_id}`} className="text-sm font-bold hover:underline block mb-0.5" style={{ color: 'var(--tf-primary)' }}>
              {p.topic_title || "Topic"}
            </Link>
            <div className="text-[10px] text-zinc-500 font-mono mb-2">
              {p.created_at ? new Date(p.created_at).toLocaleString('ro-RO', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "Recent"}
            </div>
            <div
              className="text-xs text-zinc-300 line-clamp-3 prose prose-invert max-w-none overflow-hidden"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(p.content || "") }}
            />
          </div>
        ))}
      </div>
    </WidgetCard>
  );
}

function TopicsWidget({ topics = [] }) {
  if (!topics.length) return null;
  return (
    <WidgetCard title="Topicuri create" icon={Hash} testid="widget-topics-card">
      <div className="space-y-4">
        {topics.map((t) => (
          <div key={t.id} className="relative pl-4 border-b border-white/5 pb-3 group">
            <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity shadow-[0_0_10px_rgba(6,182,212,0.5)]" />
            <Link to={`/topic/${t.id}`} className="text-sm font-bold text-cyan-400 hover:underline block mb-1">
              {t.title}
            </Link>
            <div className="text-[10px] text-zinc-400 font-mono">
              {t.created_at ? format(new Date(t.created_at), "PP") : "Recent"} · {t.post_count || 1} răspunsuri
            </div>
          </div>
        ))}
      </div>
    </WidgetCard>
  );
}
